import bigWinBounceText from "ags/components/big-win-bounce-text";
import { CustomLayers } from "ags/names";
import { BigWinThresholds } from "ags/utils/win-magnitude";
import { Orientation } from "slotworks/graphics/canvas/canvas-manager";
import canvasManager from "slotworks/graphics/canvas/canvas-manager";
import Layers from "slotworks/graphics/layers/layers";
import particleManager from "slotworks/graphics/particles/particle-manager";
import { CenterPivot } from "slotworks/graphics/pixi/group";
import Contract from "slotworks/utils/contracts/contract";
import Point from "slotworks/utils/geom/point";
import Timer from "slotworks/utils/timer";
import { Tween } from "slotworks/utils/tween";

export default class BigWinComponent {

    private showerEmitter: any;
    private borderEmitterLeft: any;
    private borderEmitterRight: any;
    private backgroundEmitter: any;

    // TODO: change from type any.
    private borderLeftTween: any;
    private borderRightTween: any;
    private rotationTween: any;
    private coinContainer: PIXI.Container;
    private sparkleContainer: PIXI.Container;
    private orientation: Orientation;
    private bigWinLevel: BigWinThresholds;

    private winTextLabel = "win_label";
    private winTextBackground = "win_bg";

    constructor(bigWinLevel: BigWinThresholds, orientation: Orientation) {
        this.coinContainer = new PIXI.Container();
        this.orientation = orientation;
        this.bigWinLevel = bigWinLevel;

        const isFreespins = !Layers.BetBar.getText(this.winTextLabel);
        if (isFreespins) {
            this.winTextLabel = "bonus_win_label";
            this.winTextBackground = "bonus_win_background";
        }

        this.createWinBoxEmitters();

        if (orientation === canvasManager.orientation) {
            this.startWinBoxEmitters();
            this.fadeOutWinText();
            bigWinBounceText.bounceText(this.bigWinLevel);
        }

        if (bigWinLevel >= BigWinThresholds.HUGE) {
            this.showerEmitter = this.startRainEmitter("bigWin_gem");
        } else if (bigWinLevel >= BigWinThresholds.BIG) {
            this.showerEmitter = this.startRainEmitter("bigWin_coin");
        }

        if (bigWinLevel >= BigWinThresholds.HUGE) {
            this.animateRays();
        }

        canvasManager.onOrientationChange.add(this.switchOrientation, this);
    }

    public setVisible(isVisible = true) {
        this.coinContainer.visible = isVisible;
        this.sparkleContainer.visible = isVisible;
    }

    public stop(immediateStop = false) {
        return new Contract((resolve) => {

            this.cleanUpTextFields();
            this.stopRainEmitters();
            this.stopWinBoxEmitters();
            this.hideRays();

            canvasManager.onOrientationChange.remove(this.switchOrientation, this);

            if (immediateStop) {
                resolve(null);
                this.setVisible(false);
                this.destroy();
                return;
            }

            // Wait for particles to die.
            Timer.setTimeout(() => {
                this.destroy();
                resolve(null);
            }, 3000);
        });
    }

    private hideRays() {
        Layers.Custom.get(CustomLayers.RAYS).setScene("default").execute();
    }

    private animateRays() {
        Layers.Custom.get(CustomLayers.RAYS).setScene("bigwin").execute();
        Layers.Custom.get(CustomLayers.RAYS).moveBehind(Layers.BetBar);

        const rays = Layers.Custom.get(CustomLayers.RAYS).getSprite("rays");
        rays.visible = true;
        CenterPivot(rays);
        this.fadeIn(rays, 0.5, 1000);

        this.rotationTween = new Tween(rays);
        this.rotationTween.to({ rotation: Math.PI * 2 }, 40000);
        this.rotationTween.repeat(Infinity);
        this.rotationTween.start();
    }

    private destroy() {
        this.sparkleContainer.destroy();
        this.borderEmitterLeft.destroy();
        this.borderEmitterRight.destroy();
        this.backgroundEmitter.destroy();

        if (this.showerEmitter) {
            this.showerEmitter.destroy();
            this.coinContainer.destroy();
        }
    }

    private stopRainEmitters() {
        if (this.showerEmitter) {
            this.showerEmitter.emit = false;
        }
    }

    private startRainEmitter(texture: string) {
        Layers.CelebrationForeground.container.addChild(this.coinContainer);
        const emitter = this.createEmitter(Layers.CelebrationForeground, texture, this.coinContainer);
        emitter.emit = true;
        return emitter;
    }

    private createEmitter(layer: Layers, emitterName: string, container: PIXI.Container, destinationLayer?: Layers, alias?: string) {

        let positionName = `${this.orientation}_${emitterName}`;

        if (alias) {
            positionName += `_${alias}`;
        }

        let position = layer.getPosition(positionName);
        if (destinationLayer) {
            position = layer.localToLocal(destinationLayer, position);
        }
        const emitterPosition = new Point(position.x, position.y);
        const emitterId = particleManager.add(emitterName, new Point(0, 0), container);
        const emitter = particleManager.get(emitterId);
        emitter.updateSpawnPos(emitterPosition.x, emitterPosition.y);
        emitter.emit = false;

        return emitter;
    }

    private switchOrientation(newOrientation: Orientation) {
        if (newOrientation === this.orientation) {
            this.startWinBoxEmitters();
            this.showWinText();
            bigWinBounceText.switchOrientation(newOrientation);
        }
    }

    private cleanUpTextFields() {
        if (canvasManager.orientation !== this.orientation) {
            return;
        }

        bigWinBounceText.fadeOut();

        this.fadeIn(Layers.BetBar.getText(this.winTextLabel));
        const backgroundSprite = Layers.BetBar.getSprite(this.winTextBackground);
        if (backgroundSprite) {
            this.fadeIn(backgroundSprite);
        }

    }

    private showWinText() {
        Layers.BetBar.getText(this.winTextLabel).visible = true;
        const backgroundSprite = Layers.BetBar.getSprite(this.winTextBackground);
        if (backgroundSprite) {
            backgroundSprite.visible = true;
        }
    }

    private fadeOutWinText() {
        this.fadeOut(Layers.BetBar.getText(this.winTextLabel));
        const backgroundSprite = Layers.BetBar.getSprite(this.winTextBackground);
        if (backgroundSprite) {
            this.fadeOut(backgroundSprite);
        }
    }

    private fadeIn(displayObject: PIXI.DisplayObject, targetAlpha = 1, duration = 300) {
        displayObject.alpha = 0;
        const exitTween = new Tween(displayObject);
        exitTween.to({ alpha: targetAlpha }, duration);
        exitTween.start();
    }

    private fadeOut(displayObject: PIXI.DisplayObject, duration = 300) {
        const exitTween = new Tween(displayObject);
        exitTween.to({ alpha: 0 }, duration);
        exitTween.start();
    }

    private createWinBoxEmitters() {
        const layer = Layers.Custom.get(CustomLayers.BigWinEmitter);
        this.sparkleContainer = new PIXI.Container();
        Layers.BetBar.add(this.sparkleContainer);
        this.backgroundEmitter = this.createEmitter(layer, "bigWinBackground", this.sparkleContainer, Layers.BetBar);
        this.borderEmitterLeft = this.createEmitter(layer, "winBoxSparkle", this.sparkleContainer, Layers.BetBar, "borderLeft");
        this.borderEmitterRight = this.createEmitter(layer, "winBoxSparkle", this.sparkleContainer, Layers.BetBar, "borderRight");

        this.borderLeftTween = this.tweenBorderEmitter(this.borderEmitterLeft, this.getPathPoints("left"));
        this.borderRightTween = this.tweenBorderEmitter(this.borderEmitterRight, this.getPathPoints("right"));
    }

    private startWinBoxEmitters() {
        this.backgroundEmitter.emit = true;
        this.borderEmitterLeft.emit = true;
        this.borderEmitterRight.emit = true;
    }

    private stopWinBoxEmitters() {
        this.backgroundEmitter.emit = false;
        this.borderEmitterLeft.emit = false;
        this.borderEmitterRight.emit = false;
        this.borderLeftTween.stop();
        this.borderRightTween.stop();
    }

    private tweenBorderEmitter(emitter: any, path: Point[]) {
        const x = path.map((point) => point.x);
        x.unshift(emitter.spawnPos.x);

        const y = path.map((point) => point.y);
        y.unshift(emitter.spawnPos.y);

        const emitterPosition = new Point(x[0], y[0]);
        emitter.updateSpawnPos(emitterPosition.x, emitterPosition.y);
        emitter.resetPositionTracking();

        const tween = new Tween(emitterPosition);
        tween.to({ x, y }, 1000);
        tween.repeat(Infinity);

        tween.onUpdate(() => {
            emitter.updateSpawnPos(emitterPosition.x, emitterPosition.y);

            if (this.orientation !== canvasManager.orientation) {
                return;
            }
        });

        tween.start();
        return tween;
    }

    private getPathPoints(direction: string) {
        const pathPointIds = [0, 1, 2];
        const layer = Layers.Custom.get(CustomLayers.BigWinEmitter);
        return pathPointIds.map((index) => {
            const position = layer.localToLocal(Layers.BetBar, layer.getPosition(`${this.orientation}_border_${direction}_path_${index}`));
            return new Point(position.x, position.y);
        });
    }
}
